/**
 * AISectionsButton - Individual question button for AI Sections
 *
 * Pill-shaped button with icon, text, and arrow that triggers the Rose widget
 * when clicked. Includes shimmer animation on first reveal and staggered entrance.
 */

import { useRef } from 'react';
import { useRevealAnimation } from '../../hooks/ui/useRevealAnimation';
import { getContrastingTextColor } from '../../utils/ui/colorUtils';
import ArrowRightIcon from '../ArrowRightIcon';
import SparkleIcon from '../SparkleIcon';

export interface AISectionsButtonProps {
    /** The question text to display and send when clicked */
    question: string;
    /** Primary color for styling (from site config or section config) */
    primaryColor?: string;
    /** Background color for the button */
    backgroundColor?: string;
    /** Text color (auto-calculated if not provided) */
    textColor?: string;
    /** Font size for button text */
    fontSize?: string;
    /** Font weight for button text */
    fontWeight?: string;
    /** Size of sparkle icon in pixels */
    iconSize?: number;
    /** Size of arrow icon in pixels */
    arrowSize?: number;
    /** Whether to show the button (controls entrance animation) */
    show?: boolean;
    /** Delay before showing (for staggered animation) */
    animationDelay?: number;
    /** Whether to show shimmer effect on first reveal */
    showShimmer?: boolean;
    /** Use inline style for background (explicit styling > customCSS > default) */
    useInlineBackground?: boolean;
    /** Use inline style for text color (explicit styling > customCSS > default) */
    useInlineTextColor?: boolean;
    /** Use inline style for font size (explicit styling > customCSS > default) */
    useInlineFontSize?: boolean;
    /** Use inline style for font weight (explicit styling > customCSS > default) */
    useInlineFontWeight?: boolean;
    /** Click handler */
    onClick: (question: string) => void;
}

export function AISectionsButton({
    question,
    primaryColor,
    backgroundColor = '#FFFFFF',
    textColor,
    fontSize = '20px',
    fontWeight = '400',
    iconSize = 20,
    arrowSize = 24,
    show = true,
    animationDelay = 0,
    showShimmer = true,
    useInlineBackground = true,
    useInlineTextColor = true,
    useInlineFontSize = true,
    useInlineFontWeight = true,
    onClick,
}: AISectionsButtonProps) {
    const containerRef = useRef<HTMLButtonElement>(null);

    // Use shared reveal animation hook
    const { isRevealed, showShimmerEffect, cssSparkleColor } = useRevealAnimation({
        show,
        animationDelay,
        showShimmer,
        containerRef,
    });

    // Compute effective text color (only when using inline styles)
    const effectiveTextColor = useInlineTextColor
        ? textColor || getContrastingTextColor(backgroundColor)
        : undefined;

    // Sparkle color: CSS variable override > primaryColor (from site config)
    const effectiveSparkleColor = cssSparkleColor || primaryColor;

    const handleClick = () => {
        onClick(question);
    };

    return (
        <button
            ref={containerRef}
            type="button"
            onClick={handleClick}
            className="ai-sections-button"
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 16px',
                borderRadius: '9999px',
                border: 'none',
                outline: 'none',
                textDecoration: 'none',
                transition: 'all 0.3s ease-out',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                position: 'relative',
                overflow: 'hidden',
                cursor: 'pointer',
                // Use inline style if explicitly set or no customCSS, otherwise let CSS win
                ...(useInlineBackground ? { background: backgroundColor } : {}),
                opacity: isRevealed ? 1 : 0,
                transform: isRevealed ? 'translateY(0)' : 'translateY(12px)',
                transitionDelay: `${animationDelay}ms`,
                pointerEvents: isRevealed ? 'auto' : 'none',
            }}
        >
            {/* Sparkle icon - uses primaryColor from site config, overridable via --ai-sections-sparkle-color CSS variable */}
            <SparkleIcon
                size={iconSize}
                color={effectiveSparkleColor}
                style={{ flexShrink: 0 }}
                className="ai-sections-icon"
            />

            {/* Question text */}
            <span
                className="ai-sections-button-text"
                style={{
                    // Use inline style if explicitly set or no customCSS, otherwise let CSS win
                    ...(useInlineTextColor ? { color: effectiveTextColor } : {}),
                    ...(useInlineFontWeight ? { fontWeight } : {}),
                    ...(useInlineFontSize ? { fontSize } : {}),
                    lineHeight: 1,
                    textAlign: 'left',
                }}
            >
                {question}
            </span>

            {/* Arrow icon - uses inline color if explicitly set or no customCSS */}
            <ArrowRightIcon
                width={arrowSize}
                height={arrowSize}
                color={useInlineTextColor ? effectiveTextColor : undefined}
                style={{ flexShrink: 0, opacity: 0.6 }}
                className="ai-sections-arrow"
            />

            {/* Shimmer effect */}
            {showShimmerEffect && (
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        pointerEvents: 'none',
                        background:
                            'linear-gradient(135deg, transparent 30%, rgba(255,255,255,0.4) 50%, transparent 70%)',
                        transform: 'translateX(-100%) translateY(-100%)',
                        animation: 'rw-diagonal-shimmer 0.8s ease-out forwards',
                        width: '200%',
                        height: '200%',
                    }}
                />
            )}
        </button>
    );
}
